export type CalendarEventRecord = {
  id: string;
  title: string;
  details: string | null;
  start: string;
  end: string;
  color: string | null;
  allDay: boolean;
  timezone: string | null;
  source: string;
  type: string;
  status: string;
  location: string | null;
  metadata: Record<string, unknown>;
};

export type CalendarEventPayload = {
  title: string;
  details?: string | null;
  start: string;
  end: string;
  color?: string | null;
  allDay?: boolean;
  timezone?: string | null;
  source?: string;
  type?: string;
  status?: string;
  location?: string | null;
  metadata?: Record<string, unknown>;
};

export type OverviewLayoutPayload = {
  order: string[];
  hidden: string[];
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const message = response.status === 422
      ? 'Please correct the calendar fields and try again.'
      : `Calendar request failed (${response.status}).`;
    throw new Error(message);
  }

  return response.json() as Promise<T>;
};

export const listCalendarEvents = async (query?: {
  start?: string;
  end?: string;
  status?: string;
  source?: string;
  type?: string;
}): Promise<CalendarEventRecord[]> => {
  const params = new URLSearchParams();

  if (query?.start) params.set('start', query.start);
  if (query?.end) params.set('end', query.end);
  if (query?.status) params.set('status', query.status);
  if (query?.source) params.set('source', query.source);
  if (query?.type) params.set('type', query.type);

  const suffix = params.toString() ? `?${params.toString()}` : '';

  return readJson<CalendarEventRecord[]>(
    await fetch(`/api/v1/secure/calendar/events${suffix}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );
};

export const createCalendarEvent = async (payload: CalendarEventPayload): Promise<CalendarEventRecord> => {
  const response = await readJson<{ event: CalendarEventRecord }>(
    await fetch('/api/v1/secure/calendar/events', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );

  return response.event;
};

export const updateCalendarEvent = async (eventId: string, payload: Partial<CalendarEventPayload>): Promise<CalendarEventRecord> => {
  const response = await readJson<{ event: CalendarEventRecord }>(
    await fetch(`/api/v1/secure/calendar/events/${eventId}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );

  return response.event;
};

export const deleteCalendarEvent = async (eventId: string): Promise<void> => {
  await readJson<{ deleted: boolean }>(
    await fetch(`/api/v1/secure/calendar/events/${eventId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
    })
  );
};

export const fetchMyScheduleOverviewLayout = async (): Promise<OverviewLayoutPayload> => {
  const response = await readJson<{ layout: OverviewLayoutPayload }>(
    await fetch('/api/v1/secure/my-schedule/overview-layout', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return response.layout;
};

export const saveMyScheduleOverviewLayout = async (payload: OverviewLayoutPayload): Promise<OverviewLayoutPayload> => {
  const response = await readJson<{ layout: OverviewLayoutPayload }>(
    await fetch('/api/v1/secure/my-schedule/overview-layout', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );

  return response.layout;
};

export const resetMyScheduleOverviewLayout = async (): Promise<OverviewLayoutPayload> => {
  const response = await readJson<{ layout: OverviewLayoutPayload }>(
    await fetch('/api/v1/secure/my-schedule/overview-layout', {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
    })
  );

  return response.layout;
};
